/**
 * Agents API Endpoints - Subtask 2: API Endpoint Functions
 * Typed API functions for agent management operations
 */

import axios from 'axios';

import {
  Agent,
  CreateAgentRequest,
  UpdateAgentRequest,
  ListAgentsRequest,
  ListAgentsResponse,
  RestartAgentResponse,
} from '../../types/api';
import { agentApiClient } from '../agentClient';

const AGENTS_ENDPOINT = '/api/v1/agents';

/**
 * List all agents with optional filters and pagination
 */
export async function listAgents(
  filters?: ListAgentsRequest
): Promise<ListAgentsResponse> {
  try {
    const params = new URLSearchParams();

    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.status) params.append('status', filters.status);
    if (filters?.site_id) params.append('site_id', filters.site_id);
    if (filters?.tenant_id) params.append('tenant_id', filters.tenant_id);
    if (filters?.search) params.append('search', filters.search);

    const response = await agentApiClient.get<ListAgentsResponse>(
      `${AGENTS_ENDPOINT}`,
      { params }
    );

    return response;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Get a single agent by ID
 */
export async function getAgent(id: string): Promise<Agent> {
  try {
    if (!id || typeof id !== 'string') {
      throw new Error('id is required');
    }
    const response = await agentApiClient.get<Agent>(
      AGENTS_ENDPOINT + '/' + id
    );
    return response;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Create a new agent
 */
export async function createAgent(data: CreateAgentRequest): Promise<Agent> {
  try {
    if (!data || typeof data !== 'object') {
      throw new Error('payload is required');
    }
    if (data.device_id && typeof data.device_id !== 'string') {
      throw new Error('device_id type must be string');
    }
    // Inject default agent_type if missing, as backend requires it
    const payload = { ...data, agent_type: data.agent_type || 'video-clipper' };
    const response = await agentApiClient.post<Agent>(
      `${AGENTS_ENDPOINT}`,
      payload
    );
    return response;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Update an existing agent
 */
export async function updateAgent(
  id: string,
  data: UpdateAgentRequest
): Promise<Agent> {
  try {
    const response = await agentApiClient.put<Agent>(
      `${AGENTS_ENDPOINT}/${id}`,
      data
    );
    return response;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Restart an agent
 */
export async function restartAgent(id: string): Promise<RestartAgentResponse> {
  try {
    const response = await agentApiClient.post<RestartAgentResponse>(
      `${AGENTS_ENDPOINT}/${id}/restart`
    );
    return response;
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Delete an agent
 */
export async function deleteAgent(id: string): Promise<void> {
  try {
    await agentApiClient.delete(`${AGENTS_ENDPOINT}/${id}`);
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Get agent metrics
 */
export async function getAgentMetrics(id: string) {
  try {
    const response = await agentApiClient.get(
      `${AGENTS_ENDPOINT}/${id}/metrics`
    );
    return response;
  } catch (error) {
    throw handleApiError(error);
  }
}

// --- Pending Agents (ZTP) ---

export interface PendingAgent {
  fingerprint: string;
  hostname: string;
  ip_address: string;
  hardware_info: any;
  status: string;
  created_at: string;
  updated_at?: string;
  last_seen?: string;
  last_seen_at?: string;
}

export interface ApproveAgentRequest {
  fingerprint: string;
  name: string;
  tenant_id: string;
  site_id: string;
  agent_type?: string;
  nvr_host?: string;
  s3_endpoint?: string;
}

/**
 * List all pending agents
 */
export async function getPendingAgents(): Promise<PendingAgent[]> {
  try {
    // Delegate to the client method so every consumer of the shared
    // ['agents','pending'] query gets the same normalized rows (it maps the
    // backend's `last_seen` field onto `last_seen_at`); a second raw fetch
    // here let whichever caller fetched last decide the cache's shape.
    return await agentApiClient.listPendingAgents();
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Approve a pending agent
 */
export async function approveAgent(data: ApproveAgentRequest): Promise<void> {
  try {
    await agentApiClient.post(`/api/v1/agents/approve`, data);
  } catch (error) {
    throw handleApiError(error);
  }
}

/**
 * Reject a pending agent
 */
export async function rejectAgent(fingerprint: string): Promise<void> {
  try {
    await agentApiClient.delete(`/api/v1/agents/pending/${fingerprint}`);
  } catch (error) {
    // If it's 404, it might already be gone, which is fine
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      return;
    }
    throw handleApiError(error);
  }
}

/**
 * Handle API errors with proper formatting
 */
function handleApiError(error: unknown): Error {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message || error.message;
    const status = error.response?.status;

    if (status === 401) {
      return new Error(`Unauthorized: ${message}`);
    } else if (status === 403) {
      return new Error(`Forbidden: ${message}`);
    } else if (status === 404) {
      return new Error(`Not Found: ${message}`);
    } else if (status === 500) {
      return new Error(`Server Error: ${message}`);
    }

    return new Error(message || 'An error occurred');
  }

  return error instanceof Error ? error : new Error('Unknown error');
}

export const getVersionDistribution = async () => {
  const response = await agentApiClient.get(
    '/api/v1/agents/distributions/versions'
  );
  return response;
};
